import type { AUTH_ERROR_CODES } from "@amtana/auth-contract";
import type { ErrorCopyFor, ExplainedError } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// What a person reads when signing in or up fails (packages/module ADR-001),
// in English and Spanish, read in the active language when the error shows.

const EN = {
  invalidCredentialsTitle: "Incorrect email or password",
  invalidCredentialsDescription: "Check both and try again.",
  emailTakenTitle: "An account with this email already exists",
  emailTakenDescription: "Sign in instead, or use another email.",
  throwawayEmailTitle: "Use a permanent email address",
  throwawayEmailDescription: "Temporary inboxes cannot open an account. Use your work or personal email.",
  challengeFailedTitle: "We could not confirm you are a person",
  challengeFailedDescription: "Reload the page and try again.",
  ssoRequiredTitle: "Your company signs in with single sign-on",
  ssoRequiredDescription: "Continue with your company's provider from the sign-in page.",
  ssoNotAdmittedTitle: "Your company doesn't let new people join this way",
  ssoNotAdmittedDescription: "Ask your workspace admin to invite you.",
  verificationInvalidTitle: "This verification link is not valid",
  verificationInvalidDescription: "It may already have been used. Request a new one.",
  verificationExpiredTitle: "This verification link has expired",
  verificationExpiredDescription: "Request a new one to confirm your address.",
  verificationRateLimitedTitle: "A verification link was already sent",
  verificationRateLimitedDescription: "Wait a bit before requesting another.",
  passwordResetInvalidTitle: "This password reset link is not valid",
  passwordResetInvalidDescription: "It may already have been used. Request a new one.",
  passwordResetExpiredTitle: "This password reset link has expired",
  passwordResetExpiredDescription: "Request a new one to reset your password.",
  currentPasswordInvalidTitle: "Your current password is incorrect",
  currentPasswordInvalidDescription: "Check it and try again.",
  sessionNotFoundTitle: "That session no longer exists",
  sessionNotFoundDescription: "It may have already been ended.",
  passkeyChallengeInvalidTitle: "That passkey ceremony is not valid",
  passkeyChallengeInvalidDescription: "Please try again.",
  passkeyChallengeExpiredTitle: "That passkey ceremony took too long",
  passkeyChallengeExpiredDescription: "Please try again.",
  passkeyVerificationFailedTitle: "That passkey could not be verified",
  passkeyVerificationFailedDescription: "Please try again, or use another way to sign in.",
  passkeyCounterReusedTitle: "This passkey looks like it was copied to another device",
  passkeyCounterReusedDescription: "Remove it from My account and add it again.",
  passkeyNotFoundTitle: "That passkey was not found",
  passkeyNotFoundDescription: "It may already have been removed.",
  emailChangeInvalidTitle: "This confirmation link is not valid",
  emailChangeInvalidDescription: "It may already have been used. Start the change again from My account.",
  emailChangeExpiredTitle: "This confirmation link has expired",
  emailChangeExpiredDescription: "Start the change again from My account.",
  googleSignInInvalidTitle: "This Google sign-in has expired",
  googleSignInInvalidDescription: "It took too long or was already used. Continue with Google again.",
  googleSignInFailedTitle: "Google could not confirm who you are",
  googleSignInFailedDescription: "Please try again, or use another way to sign in.",
  googleEmailUnverifiedTitle: "Google has not verified this address",
  googleEmailUnverifiedDescription: "Sign in with your email and password, or a passkey.",
  googleAccountTakenTitle: "This Google account is already in use",
  googleAccountTakenDescription: "It is connected to another {product} account. Disconnect it there first.",
  linkedinSignInInvalidTitle: "This LinkedIn sign-in has expired",
  linkedinSignInInvalidDescription: "It took too long or was already used. Continue with LinkedIn again.",
  linkedinSignInFailedTitle: "LinkedIn could not confirm who you are",
  linkedinSignInFailedDescription: "Please try again, or use another way to sign in.",
  linkedinEmailUnverifiedTitle: "LinkedIn has not verified this address",
  linkedinEmailUnverifiedDescription: "Sign in with your email and password, or a passkey.",
  linkedinAccountTakenTitle: "This LinkedIn account is already in use",
  linkedinAccountTakenDescription: "It is connected to another {product} account. Disconnect it there first.",
  oidcIssuerRefusedTitle: "That issuer is a simulator",
  oidcIssuerRefusedDescription: "Production signs in through the real provider. Use its own issuer address.",
  erasureBlockedTitle: "An organization still needs you",
  erasureBlockedDescription: "Remove its other members, transfer your role, or leave it first.",
  erasureConfirmationMismatchTitle: "That does not match your email",
  erasureConfirmationMismatchDescription: "Type your own email address exactly to confirm.",
} as const;

const ES: Translation<typeof EN> = {
  invalidCredentialsTitle: "Correo o contraseña incorrectos",
  invalidCredentialsDescription: "Revisa ambos e inténtalo de nuevo.",
  emailTakenTitle: "Ya existe una cuenta con este correo",
  emailTakenDescription: "Inicia sesión, o usa otro correo.",
  throwawayEmailTitle: "Usa una dirección de correo permanente",
  throwawayEmailDescription:
    "Los buzones temporales no pueden abrir una cuenta. Usa tu correo del trabajo o personal.",
  challengeFailedTitle: "No pudimos confirmar que eres una persona",
  challengeFailedDescription: "Recarga la página e inténtalo de nuevo.",
  ssoRequiredTitle: "Tu empresa inicia sesión con inicio de sesión único",
  ssoRequiredDescription: "Continúa con el proveedor de tu empresa desde la página de inicio de sesión.",
  ssoNotAdmittedTitle: "Tu empresa no permite que personas nuevas se unan de esta forma",
  ssoNotAdmittedDescription: "Pide al administrador de tu espacio que te invite.",
  verificationInvalidTitle: "Este enlace de verificación no es válido",
  verificationInvalidDescription: "Puede que ya se haya usado. Pide uno nuevo.",
  verificationExpiredTitle: "Este enlace de verificación venció",
  verificationExpiredDescription: "Pide uno nuevo para confirmar tu dirección.",
  verificationRateLimitedTitle: "Ya se envió un enlace de verificación",
  verificationRateLimitedDescription: "Espera un poco antes de pedir otro.",
  passwordResetInvalidTitle: "Este enlace para restablecer la contraseña no es válido",
  passwordResetInvalidDescription: "Puede que ya se haya usado. Pide uno nuevo.",
  passwordResetExpiredTitle: "Este enlace para restablecer la contraseña venció",
  passwordResetExpiredDescription: "Pide uno nuevo para restablecer tu contraseña.",
  currentPasswordInvalidTitle: "Tu contraseña actual es incorrecta",
  currentPasswordInvalidDescription: "Revísala e inténtalo de nuevo.",
  sessionNotFoundTitle: "Esa sesión ya no existe",
  sessionNotFoundDescription: "Puede que ya se haya cerrado.",
  passkeyChallengeInvalidTitle: "Esa verificación de llave de acceso no es válida",
  passkeyChallengeInvalidDescription: "Inténtalo de nuevo.",
  passkeyChallengeExpiredTitle: "Esa verificación de llave de acceso tardó demasiado",
  passkeyChallengeExpiredDescription: "Inténtalo de nuevo.",
  passkeyVerificationFailedTitle: "No se pudo verificar esa llave de acceso",
  passkeyVerificationFailedDescription: "Inténtalo de nuevo, o usa otra forma de iniciar sesión.",
  passkeyCounterReusedTitle: "Esta llave de acceso parece haberse copiado a otro dispositivo",
  passkeyCounterReusedDescription: "Quítala de Mi cuenta y vuelve a añadirla.",
  passkeyNotFoundTitle: "No se encontró esa llave de acceso",
  passkeyNotFoundDescription: "Puede que ya se haya quitado.",
  emailChangeInvalidTitle: "Este enlace de confirmación no es válido",
  emailChangeInvalidDescription: "Puede que ya se haya usado. Empieza el cambio de nuevo desde Mi cuenta.",
  emailChangeExpiredTitle: "Este enlace de confirmación venció",
  emailChangeExpiredDescription: "Empieza el cambio de nuevo desde Mi cuenta.",
  googleSignInInvalidTitle: "Este inicio de sesión con Google venció",
  googleSignInInvalidDescription: "Tardó demasiado o ya se usó. Continúa con Google de nuevo.",
  googleSignInFailedTitle: "Google no pudo confirmar quién eres",
  googleSignInFailedDescription: "Inténtalo de nuevo, o usa otra forma de iniciar sesión.",
  googleEmailUnverifiedTitle: "Google no ha verificado esta dirección",
  googleEmailUnverifiedDescription: "Inicia sesión con tu correo y contraseña, o con una llave de acceso.",
  googleAccountTakenTitle: "Esta cuenta de Google ya está en uso",
  googleAccountTakenDescription: "Está conectada a otra cuenta de {product}. Desconéctala allí primero.",
  linkedinSignInInvalidTitle: "Este inicio de sesión con LinkedIn venció",
  linkedinSignInInvalidDescription: "Tardó demasiado o ya se usó. Continúa con LinkedIn de nuevo.",
  linkedinSignInFailedTitle: "LinkedIn no pudo confirmar quién eres",
  linkedinSignInFailedDescription: "Inténtalo de nuevo, o usa otra forma de iniciar sesión.",
  linkedinEmailUnverifiedTitle: "LinkedIn no ha verificado esta dirección",
  linkedinEmailUnverifiedDescription: "Inicia sesión con tu correo y contraseña, o con una llave de acceso.",
  linkedinAccountTakenTitle: "Esta cuenta de LinkedIn ya está en uso",
  linkedinAccountTakenDescription: "Está conectada a otra cuenta de {product}. Desconéctala allí primero.",
  oidcIssuerRefusedTitle: "Ese emisor es un simulador",
  oidcIssuerRefusedDescription:
    "Producción inicia sesión con el proveedor real. Usa la dirección de su propio emisor.",
  erasureBlockedTitle: "Una organización todavía te necesita",
  erasureBlockedDescription: "Quita a sus otros miembros, transfiere tu rol, o sal de ella primero.",
  erasureConfirmationMismatchTitle: "Eso no coincide con tu correo",
  erasureConfirmationMismatchDescription: "Escribe tu propia dirección de correo exactamente para confirmar.",
};

export const AUTH_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const AUTH_ERROR_COPY = {
  auth_invalid_credentials: {
    title: phrase(AUTH_ERROR_CATALOGS, "invalidCredentialsTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "invalidCredentialsDescription"),
  },
  auth_email_taken: {
    title: phrase(AUTH_ERROR_CATALOGS, "emailTakenTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "emailTakenDescription"),
  },
  auth_throwaway_email: {
    title: phrase(AUTH_ERROR_CATALOGS, "throwawayEmailTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "throwawayEmailDescription"),
  },
  auth_challenge_failed: {
    title: phrase(AUTH_ERROR_CATALOGS, "challengeFailedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "challengeFailedDescription"),
  },
  auth_sso_required: {
    title: phrase(AUTH_ERROR_CATALOGS, "ssoRequiredTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "ssoRequiredDescription"),
  },
  auth_sso_not_admitted: {
    title: phrase(AUTH_ERROR_CATALOGS, "ssoNotAdmittedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "ssoNotAdmittedDescription"),
  },
  auth_verification_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "verificationInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "verificationInvalidDescription"),
  },
  auth_verification_expired: {
    title: phrase(AUTH_ERROR_CATALOGS, "verificationExpiredTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "verificationExpiredDescription"),
  },
  auth_verification_rate_limited: {
    title: phrase(AUTH_ERROR_CATALOGS, "verificationRateLimitedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "verificationRateLimitedDescription"),
  },
  auth_password_reset_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "passwordResetInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passwordResetInvalidDescription"),
  },
  auth_password_reset_expired: {
    title: phrase(AUTH_ERROR_CATALOGS, "passwordResetExpiredTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passwordResetExpiredDescription"),
  },
  auth_current_password_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "currentPasswordInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "currentPasswordInvalidDescription"),
  },
  auth_session_not_found: {
    title: phrase(AUTH_ERROR_CATALOGS, "sessionNotFoundTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "sessionNotFoundDescription"),
  },
  auth_passkey_challenge_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "passkeyChallengeInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passkeyChallengeInvalidDescription"),
  },
  auth_passkey_challenge_expired: {
    title: phrase(AUTH_ERROR_CATALOGS, "passkeyChallengeExpiredTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passkeyChallengeExpiredDescription"),
  },
  auth_passkey_verification_failed: {
    title: phrase(AUTH_ERROR_CATALOGS, "passkeyVerificationFailedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passkeyVerificationFailedDescription"),
  },
  auth_passkey_counter_reused: {
    title: phrase(AUTH_ERROR_CATALOGS, "passkeyCounterReusedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passkeyCounterReusedDescription"),
  },
  auth_passkey_not_found: {
    title: phrase(AUTH_ERROR_CATALOGS, "passkeyNotFoundTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "passkeyNotFoundDescription"),
  },
  auth_email_change_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "emailChangeInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "emailChangeInvalidDescription"),
  },
  auth_email_change_expired: {
    title: phrase(AUTH_ERROR_CATALOGS, "emailChangeExpiredTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "emailChangeExpiredDescription"),
  },
  auth_google_sign_in_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "googleSignInInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "googleSignInInvalidDescription"),
  },
  auth_google_sign_in_failed: {
    title: phrase(AUTH_ERROR_CATALOGS, "googleSignInFailedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "googleSignInFailedDescription"),
  },
  auth_google_email_unverified: {
    title: phrase(AUTH_ERROR_CATALOGS, "googleEmailUnverifiedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "googleEmailUnverifiedDescription"),
  },
  auth_google_account_taken: {
    title: phrase(AUTH_ERROR_CATALOGS, "googleAccountTakenTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "googleAccountTakenDescription"),
  },
  auth_linkedin_sign_in_invalid: {
    title: phrase(AUTH_ERROR_CATALOGS, "linkedinSignInInvalidTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "linkedinSignInInvalidDescription"),
  },
  auth_linkedin_sign_in_failed: {
    title: phrase(AUTH_ERROR_CATALOGS, "linkedinSignInFailedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "linkedinSignInFailedDescription"),
  },
  auth_linkedin_email_unverified: {
    title: phrase(AUTH_ERROR_CATALOGS, "linkedinEmailUnverifiedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "linkedinEmailUnverifiedDescription"),
  },
  auth_linkedin_account_taken: {
    title: phrase(AUTH_ERROR_CATALOGS, "linkedinAccountTakenTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "linkedinAccountTakenDescription"),
  },
  auth_oidc_issuer_refused: {
    title: phrase(AUTH_ERROR_CATALOGS, "oidcIssuerRefusedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "oidcIssuerRefusedDescription"),
  },
  auth_erasure_blocked: {
    title: phrase(AUTH_ERROR_CATALOGS, "erasureBlockedTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "erasureBlockedDescription"),
  },
  auth_erasure_confirmation_mismatch: {
    title: phrase(AUTH_ERROR_CATALOGS, "erasureConfirmationMismatchTitle"),
    description: phrase(AUTH_ERROR_CATALOGS, "erasureConfirmationMismatchDescription"),
  },
} satisfies ErrorCopyFor<typeof AUTH_ERROR_CODES>;

/** An explained failure as one line under a form field. */
export function inlineError(explained: Pick<ExplainedError, "title" | "description">): string {
  return explained.description ? `${explained.title}. ${explained.description}` : `${explained.title}.`;
}

/**
 * What the sign-in form says when a sign-in fails: an email's throttle (ADR-010 revision 2) reads
 * as the ways in that stay open, any other failure as its explained copy.
 */
export function signInFailureText(options: {
  readonly throttled: boolean;
  readonly explained: Pick<ExplainedError, "title" | "description">;
  readonly throttledCopy: string;
}): string {
  return options.throttled ? options.throttledCopy : inlineError(options.explained);
}
